/* Automatic profiling after a harvest, on the connection page's Schedule & automation tab.
 *
 * The design's card: an icon tile, the title and what it does, and a state chip; one sentence on
 * what happens now; while it is on, a bar and three counts — tables waiting, profiled, and the ones
 * that could not be profiled; then the switch.
 *
 * **The cost is stated where the decision is made.** Turning this on spends money on somebody
 * else's platform, so the confirmation in front of "Turn on" says so before anything is queued —
 * the moment a reader is deciding. Turning it off asks nothing: a confirmation in front of
 * *stopping* a cost is a dialog somebody clicks through while the bill runs.
 *
 * Deliberately in plain language. The reader is a data owner deciding about their own warehouse,
 * so nothing here says concurrency, worker, queue, admission or scan, and the figures are counts of
 * tables rather than of jobs.
 */

import { useId, useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { AutoProfile } from '@/shared/api/generated/catalog';
import { ApiError } from '@/shared/api/http';
import { personName } from '@/shared/model/person';
import { Button } from '@/shared/ui/Button';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';
import { HelpLabel } from '@/shared/ui/InfoTip';

import {
  useAutoProfile,
  useRetryFailedAutoProfiles,
  useSetAutoProfileEnabled,
} from '../api/autoProfile';
import { canRetry, presenceOf, type AutoProfilePresence } from '../model/autoProfile';

export interface AutoProfileCardProps {
  readonly connectionId: string;
  /** Whether the reader holds `catalog.dataset:profile`, which is what changing this needs. `can`
   * hides controls and protects nothing; a refused write hides them too. */
  readonly mayChange?: boolean;
}

/* Total maps on the four presences, so a fifth is a compile error here as well as in the model. */
const CHIP: Record<AutoProfilePresence['kind'], { tone: string; key: string }> = {
  unconfigured: { tone: 'off', key: 'autoProfile.state.off' },
  off: { tone: 'off', key: 'autoProfile.state.off' },
  idle: { tone: 'ok', key: 'autoProfile.state.idle' },
  working: { tone: 'info', key: 'autoProfile.state.working' },
};

const DESCRIBES: Record<AutoProfilePresence['kind'], string> = {
  unconfigured: 'autoProfile.describes.off',
  off: 'autoProfile.describes.off',
  idle: 'autoProfile.describes.idle',
  working: 'autoProfile.describes.working',
};

function ChartGlyph(): ReactElement {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 19V9M10 19V5M16 19v-7M22 19H2"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function AutoProfileCard({
  connectionId,
  mayChange = true,
}: AutoProfileCardProps): ReactElement {
  const { t } = useTranslation();
  const profile = useAutoProfile(connectionId);
  const setEnabled = useSetAutoProfileEnabled();
  const retry = useRetryFailedAutoProfiles();
  const [confirming, setConfirming] = useState(false);
  const titleId = useId();

  const frame = (chip: ReactNode, body: ReactNode): ReactElement => (
    <article className="lq-ac" aria-labelledby={titleId}>
      <header className="lq-ac__head">
        <span className="lq-ac__icon" data-tone="mint">
          <ChartGlyph />
        </span>
        <div className="lq-ac__heading">
          <HelpLabel
            subject={t('autoProfile.title')}
            help={t('help.feature.autoProfiling')}
          >
            <h2 className="lq-ac__title" id={titleId}>
              {t('autoProfile.title')}
            </h2>
          </HelpLabel>
          <p className="lq-ac__sub">{t('autoProfile.subtitle')}</p>
        </div>
        {chip}
      </header>
      {body}
    </article>
  );

  /* **A failed read is reported, never rendered as "off".** One is a claim about this source and
     the other a failure of the request; §9 records the conflation shipping three times. */
  if (profile.isError) {
    const forbidden = profile.error instanceof ApiError && profile.error.status === 403;
    return frame(
      null,
      <p className="lq-ac__text">
        {forbidden ? t('autoProfile.forbidden') : t('autoProfile.readFailed')}
      </p>,
    );
  }

  if (!profile.isSuccess) {
    return frame(null, <p className="lq-ac__muted">{t('scheduling.loading')}</p>);
  }

  const stored: AutoProfile = profile.data;
  const presence = presenceOf(stored);
  const counts = stored.queue;
  const chip = CHIP[presence.kind];

  const refusedWrite = [setEnabled.error, retry.error].some(
    (error) => error instanceof ApiError && error.status === 403,
  );
  const canChange = mayChange && !refusedWrite;

  /* The bar's three parts share one whole: what is done, what failed, and the waiting remainder
     the track shows through. Percentages of tables, never of jobs. */
  const whole = Math.max(1, counts.succeeded + counts.outstanding + counts.failed);
  const donePercent = Math.round((counts.succeeded / whole) * 100);
  const failedPercent = Math.round((counts.failed / whole) * 100);

  const figures = [
    { key: 'waiting', tone: 'info', value: counts.outstanding },
    { key: 'profiled', tone: 'ok', value: counts.succeeded },
    { key: 'failed', tone: 'danger', value: counts.failed },
  ] as const;

  return frame(
    <span className="lq-ac__chip" data-tone={chip.tone}>
      <span className="lq-ac__chipDot" aria-hidden="true" />
      {t(chip.key)}
    </span>,
    <>
      <p className="lq-ac__text">{t(DESCRIBES[presence.kind])}</p>

      {stored.enabled ? (
        <>
          <div
            className="lq-ac__bar"
            role="img"
            aria-label={t('autoProfile.barLabel', {
              done: counts.succeeded,
              failed: counts.failed,
              waiting: counts.outstanding,
            })}
          >
            <span
              className="lq-ac__barDone"
              style={{ width: `${String(donePercent)}%` }}
            />
            <span
              className="lq-ac__barFailed"
              style={{ width: `${String(failedPercent)}%` }}
            />
          </div>
          <dl className="lq-ac__counts">
            {figures.map((figure) => (
              <div key={figure.key} className="lq-ac__count" data-tone={figure.tone}>
                <dt className="lq-ac__countLabel">
                  {t(`autoProfile.count.${figure.key}`)}
                </dt>
                <dd className="lq-ac__countValue">{figure.value.toLocaleString()}</dd>
              </div>
            ))}
          </dl>
        </>
      ) : null}

      {setEnabled.isError && !refusedWrite ? (
        <p className="lq-ac__error" role="alert">
          {setEnabled.error instanceof ApiError
            ? setEnabled.error.message
            : t('autoProfile.toggleFailed')}
        </p>
      ) : null}

      {canChange ? (
        <div className="lq-ac__actions">
          {stored.enabled ? (
            <Button
              size="compact"
              busy={setEnabled.isPending}
              onClick={() => {
                setEnabled.mutate({ connectionId, enabled: false });
              }}
            >
              {t('autoProfile.turnOff')}
            </Button>
          ) : (
            <Button
              tone="primary"
              busy={setEnabled.isPending}
              onClick={() => {
                setConfirming(true);
              }}
            >
              {t('autoProfile.turnOn')}
            </Button>
          )}
          {/* `failed` is the one figure that does not resolve by itself: the platform stops trying
              once attempts are spent, so those tables wait for a person. */}
          {stored.enabled && canRetry(counts) ? (
            <Button
              size="compact"
              busy={retry.isPending}
              onClick={() => {
                retry.mutate(connectionId);
              }}
            >
              {t('autoProfile.retry')}
            </Button>
          ) : null}
          <span className="lq-ac__spacer" />
          {stored.enabled && typeof stored.configured_by === 'string' ? (
            <span className="lq-ac__by">
              {t('autoProfile.configuredBy', { who: personName(stored.configured_by) })}
            </span>
          ) : null}
        </div>
      ) : (
        <p className="lq-ac__muted">{t('autoProfile.needsPermission')}</p>
      )}

      <ConfirmDialog
        open={confirming}
        title={t('autoProfile.confirm.title')}
        message={t('autoProfile.confirm.body')}
        confirmLabel={t('autoProfile.turnOn')}
        cancelLabel={t('autoProfile.confirm.cancel')}
        busy={setEnabled.isPending}
        onCancel={() => {
          setConfirming(false);
        }}
        onConfirm={() => {
          setConfirming(false);
          setEnabled.mutate({ connectionId, enabled: true });
        }}
      />
    </>,
  );
}
